import type { StorefrontTemplateId } from "@loot-os/api-contract";
import type { ComponentType } from "react";

/** The outline of one template's home page, top to bottom, in its own coordinates. */
type Outline = ComponentType;

/** A stroked outline block; filled blocks stand for text and buttons. */
function Box(props: { x: number; y: number; w: number; h: number; r?: number; fill?: boolean }) {
  return (
    <rect
      x={props.x}
      y={props.y}
      width={props.w}
      height={props.h}
      rx={props.r ?? 2}
      fill={props.fill ? "currentColor" : "none"}
      fillOpacity={props.fill ? 0.35 : undefined}
      stroke="currentColor"
      strokeWidth={props.fill ? 0 : 1}
    />
  );
}

/** Classic: header, hero with art, three promo cards, the opens row, featured boxes, FAQ, footer. */
function ClassicOutline() {
  return (
    <>
      {/* Header: brand, nav, sign in. */}
      <Box x={8} y={6} w={22} h={4} fill />
      {[36, 50, 64].map((x) => (
        <Box key={x} x={x} y={7} w={10} h={2} fill />
      ))}
      <Box x={138} y={5} w={14} h={6} r={2} fill />
      <line x1={0} y1={16} x2={160} y2={16} stroke="currentColor" strokeOpacity={0.5} />

      {/* Hero: headline, body, two actions, art. */}
      <Box x={8} y={22} w={144} h={40} r={4} />
      <Box x={14} y={30} w={70} h={7} fill />
      <Box x={14} y={41} w={62} h={2.5} fill />
      <Box x={14} y={45} w={54} h={2.5} fill />
      <Box x={14} y={51} w={14} h={5} fill />
      <Box x={30} y={51} w={14} h={5} />
      <Box x={112} y={27} w={34} h={30} r={3} />

      {/* Promo cards: sign-up with its buttons, then card-wide links. */}
      {[8, 57, 106].map((x) => (
        <g key={x}>
          <Box x={x} y={68} w={46} h={24} r={3} />
          <Box x={x + 4} y={72} w={26} h={3} fill />
          <Box x={x + 4} y={77} w={34} h={4} fill />
        </g>
      ))}
      <Box x={12} y={85} w={38} h={3} fill />

      {/* Recent opens and featured boxes: rows of cards. */}
      <Box x={8} y={98} w={30} h={3.5} fill />
      {[8, 30, 52, 74, 96, 118, 140].map((x) => (
        <Box key={x} x={x} y={104} w={x === 140 ? 12 : 19} h={20} r={2} />
      ))}
      <Box x={8} y={130} w={38} h={3.5} fill />
      {[8, 37, 66, 95, 124].map((x) => (
        <g key={x}>
          <Box x={x} y={136} w={26} h={28} r={2} />
          <Box x={x + 3} y={158} w={20} h={3} fill />
        </g>
      ))}

      {/* FAQ: the title beside the questions. */}
      <Box x={8} y={172} w={30} h={8} fill />
      {[172, 178, 184, 190].map((y) => (
        <line
          key={y}
          x1={48}
          y1={y + 3}
          x2={152}
          y2={y + 3}
          stroke="currentColor"
          strokeOpacity={0.6}
        />
      ))}

      {/* Footer. */}
      <line x1={0} y1={200} x2={160} y2={200} stroke="currentColor" strokeOpacity={0.5} />
      <Box x={8} y={205} w={22} h={4} fill />
      {[96, 124].map((x) => (
        <g key={x}>
          <Box x={x} y={205} w={18} h={2.5} fill />
          <Box x={x} y={210} w={14} h={2.5} fill />
        </g>
      ))}
    </>
  );
}

/** Every template has an outline, or this fails to compile. */
const OUTLINES: Record<StorefrontTemplateId, Outline> = { classic: ClassicOutline };

/**
 * A storefront template as a thumbnail: the outlines of its home page's sections, in the color
 * of the text around it (`text-primary` for the selected one).
 */
export function StorefrontTemplateThumbnail({
  template,
  className,
}: {
  template: StorefrontTemplateId;
  className?: string;
}) {
  const Outline = OUTLINES[template];
  return (
    <svg viewBox="0 0 160 216" className={className} aria-hidden="true" role="presentation">
      <Outline />
    </svg>
  );
}
